/**
 * The confirmation toast for the agent execute-with-confirm protocol.
 *
 * Presentation and the "exactly one answer" state machine live here; the
 * protocol itself (IPC, execution, reporting back to the agent) lives in
 * `useAiExecutionRequests.ts`.
 *
 * Three rules this module owns:
 * - **One answer per prompt.** Whichever comes first — a button, the close
 *   button, the deadline — wins, and later ones are ignored.
 * - **Dismissing is denying.** Closing the toast without choosing still
 *   answers the agent; an agent waiting forever on a response is the worst
 *   failure mode of this protocol.
 * - **Our own deadline dismissal is not a denial.** When we time the toast
 *   out, main has already answered the request, so we stay quiet rather than
 *   send a decision it would reject.
 *
 * The toast never takes focus (it renders plain buttons, nothing autofocused),
 * so it cannot interrupt typing in the editor.
 */

import { toast } from '../../../components/ui/toaster';
import { buildExecutionPrompt, type ExecutionPromptInput } from './executionRequestPrompt';

/** Which button the user pressed — low-cardinality, nothing else recorded. */
export type ExecutionConfirmChoice = 'run' | 'always' | 'deny';

export type ExecutionConfirmAnswer = {
  approved: boolean;
  scope: 'once' | 'session';
  choice: ExecutionConfirmChoice;
};

/** Mirrors the main-process decision deadline so a stale prompt can't linger. */
export const DECISION_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Raise the prompt. `onAnswer` fires at most once; it never fires when the
 * prompt is retired by its own deadline.
 */
export function showExecutionRequestToast(
  input: ExecutionPromptInput,
  onAnswer: (answer: ExecutionConfirmAnswer) => void
): void {
  const prompt = buildExecutionPrompt(input);
  const state: { answered: boolean; expired: boolean; timer?: ReturnType<typeof setTimeout> } = {
    answered: false,
    expired: false,
  };

  const answer = (approved: boolean, scope: 'once' | 'session', choice: ExecutionConfirmChoice) => {
    if (state.answered) return;
    state.answered = true;
    if (state.timer) clearTimeout(state.timer);
    onAnswer({ approved, scope, choice });
  };

  const toastId = toast.warning({
    title: prompt.title,
    description: prompt.description,
    testId: 'exec-request-toast',
    persist: true,
    actions: [
      { label: 'Run', testId: 'exec-request-run', onClick: () => answer(true, 'once', 'run') },
      { label: 'Always this session', testId: 'exec-request-always', onClick: () => answer(true, 'session', 'always') },
      { label: 'Deny', testId: 'exec-request-deny', onClick: () => answer(false, 'once', 'deny') },
    ],
    onDismiss: () => {
      if (state.expired) return;
      answer(false, 'once', 'deny');
    },
  });

  state.timer = setTimeout(() => {
    state.expired = true;
    toast.dismiss(toastId);
  }, DECISION_TIMEOUT_MS);
}
